import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import _ from 'lodash';
import { SecurityRoleMatrix } from './SecurityRoleMatrix';
import { ModulePermission } from './SecurityRoleMatrix.types';
import { applyLockedPermissions } from './securityRolePermissionLocks';
import { Formik, FormikHelpers } from 'formik';
import { Button } from 'components/ui/button';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	Grid,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
	FormControl,
	FormLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	CircularProgress,
	Box,
	Autocomplete,
} from '@mui/material';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import styles from '../GeneralSettings.module.scss';
import CreateSecurityRole from './CreateSecurityRole';
import {
	createSecurityRole,
	updateSecurityRole,
	fetchPermissionGroups,
	getSecurityRoleTypes,
	CreateSecurityRoleRequest,
	SecurityRolePermission,
	SecurityRoleResponse,
	SecurityRoleType,
} from '../../../../../core/API/securityRoles';
import { apiService } from '../../../../../core/services/apiService';
import {
	PERMISSION_FUNCTIONS,
	getPermissions,
	Permission,
} from '../../../../../core/API/permissions';
import usePermissionCheck from '../../../../../core/hooks/usePermissionCheck';
import {
	isHiddenSecurityRole,
	SECURITY_ROLE_DESCRIPTION_MAX_LENGTH,
	SECURITY_ROLE_NAME_MAX_LENGTH,
	SECURITY_ROLE_NAME_MIN_LENGTH,
	SECURITY_ROLE_NAME_PATTERN,
	SECURITY_ROLE_VALIDATION_MESSAGES,
} from '../constants';
import useUserDataStore from '../../../../../store/useUserDataStore';
import useSettingsStore from 'store/useSettingsStore';
import { useResetSettingsScroll } from '../../../utils/resetSettingsScroll';

export interface SecurityRolesProps {
	handleFocusChange?: (
		focus:
			| 'organization'
			| 'workplace'
			| 'user'
			| 'organizational_hierarchy'
			| 'security_role'
			| null
	) => void;
	inFocus?:
		| 'organization'
		| 'workplace'
		| 'user'
		| 'organizational_hierarchy'
		| 'security_role'
		| 'partners'
		| null;
}

interface SecurityRole {
	id: number;
	name: string;
	description?: string;
	workplace?: string;
	isSystemRole?: boolean;
	securityRoleType?: string;
	permissions: ModulePermission[];
}

// ... PermissionGroup interface removed (not used)

const getPermissionMatrix = (func: SecurityRolePermission) => {
	const permissionMatrix: Record<string, boolean> = {};
	for (const key of PERMISSION_FUNCTIONS) {
		permissionMatrix[key] = func[key] || false;
	}
	return permissionMatrix;
};

const getDefaultPermissionMatrix = () => {
	const permissionMatrix: Record<string, boolean> = {};
	for (const key of PERMISSION_FUNCTIONS) {
		permissionMatrix[key] = false;
	}
	return permissionMatrix;
};

const isCurrentUsersSecurityRole = (
	currentUserRole: { id?: number | string; '@id'?: string } | null,
	roleId: number | string,
): boolean => {
	if (!currentUserRole) return false;
	return (
		Number(currentUserRole.id) === Number(roleId) ||
		currentUserRole['@id'] === `/api/security_roles/${roleId}`
	);
};

function SecurityRoles(props: SecurityRolesProps) {
	const { handleFocusChange, inFocus } = props;
	const queryClient = useQueryClient();
	const { hasEditPermission, hasViewPermission, hasDeletePermission } = usePermissionCheck();
	const canViewSecurity = hasViewPermission('Security Roles');
	const canEditSecurity = hasEditPermission('Security Roles');
	const canDeleteSecurity = hasDeletePermission('Security Roles');
	const permissionGroups = useRef<Map<string, string>>(new Map());
	const { showToast } = useToastContext();

	// Map permissions using permission groups
	const mapSecurityRolePermissionsToModulePermissions = useCallback(
		(permissions: SecurityRolePermission[]): ModulePermission[] => {
			const groupedPermissions = _.groupBy(
				permissions,
				(perm) => permissionGroups.current.get(perm.permission.permissionGroup) || 'Other'
			);

			return Object.keys(groupedPermissions).map((module) => ({
				module,
				functions: groupedPermissions[module].map((func) => ({
					id: func.permission.id,
					name: func.permission.label,
					permissions: getPermissionMatrix(func),
				})),
			}));
		},
		[permissionGroups]
	);

	// Map security roles using the permission mapping
	const mapPermissionsToModulePermissions = useCallback(
		(securityRolesResponse: SecurityRoleResponse[]): SecurityRole[] => {
			const finalSecurityRoles = [];
			for (const securityRole of securityRolesResponse) {
				const mappedPermissions = mapSecurityRolePermissionsToModulePermissions(
					securityRole.securityRolePermission
				);
				const {
					createdAt,
					createdBy,
					deleted,
					id,
					updatedAt,
					updatedBy,
					workplace,
					name,
					description,
					securityRoleType,
				} = securityRole;
				finalSecurityRoles.push({
					id,
					name,
					createdAt,
					createdBy,
					deleted,
					updatedAt,
					updatedBy,
					workplace,
					description,
					securityRoleType: securityRoleType?.['@id'] || undefined,
					// Map isSystemRole from API if present. Default to false.
					isSystemRole: (securityRole as any).isSystemRole || false,
					permissions: mappedPermissions,
				});
			}

			// Sort roles alphabetically (case-insensitive) before returning
			return finalSecurityRoles.sort((a, b) =>
				(a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
			);
		},
		[mapSecurityRolePermissionsToModulePermissions]
	);

	// Helpers to parse workplace ids from stored values and role.workplace strings
	const parseLocalWorkplaceId = () => {
		const raw = localStorage.getItem('workplace-id');
		if (!raw) return null;
		try {
			const parsed = JSON.parse(raw);
			if (typeof parsed === 'number') return parsed;
		} catch (e) {
			// ignore
		}
		const num = Number(raw);
		return isNaN(num) ? null : num;
	};

	const getWorkplaceIdFromString = (workplace?: string | null) => {
		if (!workplace) return null;
		const s = String(workplace);
		const parts = s.split('/').filter(Boolean);
		const last = parts.length ? parts[parts.length - 1] : s;
		const num = Number(last);
		return isNaN(num) ? null : num;
	};

	const rolesReducer = (
		state: SecurityRole[],
		action:
			| { type: 'UPDATE_PERMISSIONS'; roleId: number; permissions: ModulePermission[] }
			| { type: 'UPDATE_ROLE'; roleId: number; role: Partial<SecurityRole> }
			| { type: 'DELETE_ROLE'; roleId: number }
			| { type: 'SET_ROLES'; roles: SecurityRole[] }
			| { type: 'ADD_ROLE'; role: SecurityRole }
	) => {
		switch (action.type) {
			case 'UPDATE_PERMISSIONS':
				return state.map((role) =>
					role.id === action.roleId ? { ...role, permissions: action.permissions } : role
				);

			case 'UPDATE_ROLE':
				return state.map((role) =>
					role.id === action.roleId ? { ...role, ...action.role } : role
				);
			case 'DELETE_ROLE':
				return state.filter((role) => role.id !== action.roleId);
			case 'SET_ROLES':
				// Ensure roles are always sorted when setting
				return [...action.roles].sort((a, b) =>
					(a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
				);
			case 'ADD_ROLE':
				// Insert the new role and keep list sorted
				return [...state, action.role].sort((a, b) =>
					(a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
				);
			default:
				return state;
		}
	};
	const [roles, dispatchRoles] = React.useReducer(rolesReducer, []);
	const [rolesLoading, setRolesLoading] = useState<boolean>(false);
	const [rolesError, setRolesError] = useState<string | null>(null);

	const fetchSecurityRolesCached = useSettingsStore((s) => s.fetchSecurityRoles);
	const removeSecurityRoleFromStore = useSettingsStore((s) => s.removeSecurityRole);

	// Visible roles (hide roles that contain the exact substring 'BYOD')
	const visibleRoles = (roles || []).filter(
		(r) => !isHiddenSecurityRole(r?.name) && !(r?.name && String(r.name).includes('BYOD'))
	);
	const [createRoleLoading, setCreateRoleLoading] = useState<boolean>(false);
	const [createRoleError, setCreateRoleError] = useState<string | null>(null);
	const [updateRoleLoading, setUpdateRoleLoading] = useState<boolean>(false);
	const [updateRoleError, setUpdateRoleError] = useState<string | null>(null);

	const [viewMode, setViewMode] = useState<'list' | 'create' | 'show' | 'edit'>('list');
	useResetSettingsScroll(viewMode);
	const [selectedRole, setSelectedRole] = useState<SecurityRole | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [roleToDelete, setRoleToDelete] = useState<SecurityRole | null>(null);
	const [allPermissions, setAllPermissions] = useState<ModulePermission[]>([]);
	const [securityRoleTypes, setSecurityRoleTypes] = useState<SecurityRoleType[]>([]);
	const [loadingEditPermissions, setLoadingEditPermissions] = useState<boolean>(false);
	const [_loadingPermissions, setLoadingPermissions] = useState<boolean>(false);

	useEffect(() => {
		const loadPermissionGroupsAndPermissions = async () => {
			setLoadingPermissions(true);
			try {
				// Fetch permission groups first
				const groups = await fetchPermissionGroups();
				permissionGroups.current = groups;

				// Fetch all available permissions and role types
				const [permissionsData, roleTypesData] = await Promise.all([
					getPermissions(),
					getSecurityRoleTypes(),
				]);
				const availablePermissions = permissionsData.items || [];
				setSecurityRoleTypes(roleTypesData || []);

				// Group permissions by permission group
				const groupedPermissions = _.groupBy(availablePermissions, (perm: Permission) => {
					return perm.permissionGroup?.label || 'Other';
				});

				// Create complete permission structure with all available permissions
				const completePermissions: ModulePermission[] = Object.keys(groupedPermissions).map(
					(module) => ({
						module,
						functions: groupedPermissions[module].map((perm: Permission) => ({
							id: perm.id,
							name: perm.label,
							permissions: getDefaultPermissionMatrix(),
						})),
					})
				);

				setAllPermissions(completePermissions);
			} catch (error) {
				console.error('Failed to fetch permission groups and permissions:', error);
			} finally {
				setLoadingPermissions(false);
			}
		};
		loadPermissionGroupsAndPermissions();
	}, []);

	useEffect(() => {
		const fetchRolesAndPermissions = async () => {
			setRolesLoading(true);
			try {
				// Uses shared settings cache — skips network when already loaded
				await fetchSecurityRolesCached();
				const rolesData = useSettingsStore.getState().securityRoles;

				const mappedRoles = mapPermissionsToModulePermissions(rolesData) || [];

				// Filter roles to only include those for the current workplace
				// Show system roles regardless of workplace (system roles dominate workplace check)
				const currentWorkplaceId = parseLocalWorkplaceId();
				const filteredRoles = mappedRoles.filter((r) => {
					if (isHiddenSecurityRole(r?.name)) return false;
					// Hide roles that contain the exact substring 'BYOD' (case-sensitive)
					if (r?.name && r.name.includes('BYOD')) return false;
					// Always include system roles
					if ((r as any).isSystemRole) return true;
					const roleWpId = getWorkplaceIdFromString(r.workplace as any);
					// If role has no workplace (organization-level), include
					if (roleWpId === null) return true;
					// If we don't know the current workplace, exclude role-specific workplace entries
					if (currentWorkplaceId === null) return false;
					return roleWpId === currentWorkplaceId;
				});

				dispatchRoles({
					type: 'SET_ROLES',
					roles: filteredRoles,
				});

				setRolesLoading(false);
			} catch (error) {
				setRolesError('Failed to load security roles');
				setRolesLoading(false);
			}
		};
		fetchRolesAndPermissions();
	}, [inFocus, mapPermissionsToModulePermissions, fetchSecurityRolesCached]);

	const handleDeleteClick = (role: SecurityRole) => {
		setRoleToDelete(role);
		setDeleteDialogOpen(true);
	};

	const handleDeleteConfirm = async () => {
		if (roleToDelete) {
			try {
				// Send delete request to API
				await apiService.delete(`/security_roles/${roleToDelete.id}`);

				// Update local state + shared settings store
				dispatchRoles({ type: 'DELETE_ROLE', roleId: roleToDelete.id });
				removeSecurityRoleFromStore(roleToDelete.id);

				setDeleteDialogOpen(false);
				setRoleToDelete(null);
				showToast('success', 'Security role deleted successfully');
			} catch (error: any) {
				console.error('Failed to delete security role:', error);
				const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete security role';
				showToast('error', `${errorMessage}. The security role might be assigned to users`);
				setDeleteDialogOpen(false);
				setRoleToDelete(null);
			}
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setRoleToDelete(null);
	};

	const handleCreateClick = () => {
		handleFocusChange?.('security_role');
		setViewMode('create');
	};

	const handleBackClick = () => {
		handleFocusChange?.(null);
		setViewMode('list');
		setSelectedRole(null);
	};

	const _handleShowClick = (role: SecurityRole) => {
		handleFocusChange?.('security_role');
		setSelectedRole(role);
		setViewMode('show');
	};

	const handleEditClick = (role: SecurityRole) => {
		// Do not allow editing system roles
		if (role.isSystemRole) {
			return;
		}

		handleFocusChange?.('security_role');
		setLoadingEditPermissions(true);

		// Use prefetched permissions and merge with role's existing permissions
		const completePermissions: ModulePermission[] = allPermissions.map((module) => ({
			...module,
			functions: module.functions.map((func) => {
				// Find if this permission exists in the role's current permissions
				let existingPermissions = getDefaultPermissionMatrix();

				for (const roleMod of role.permissions) {
					for (const roleFunc of roleMod.functions) {
						if (roleFunc.id === func.id) {
							existingPermissions = roleFunc.permissions;
							break;
						}
					}
				}

				return {
					...func,
					permissions: existingPermissions,
				};
			}),
		}));

		setSelectedRole({ ...role, permissions: applyLockedPermissions(completePermissions) });
		setViewMode('edit');
		setLoadingEditPermissions(false);
	};

	const handlePermissionChange = (updated: ModulePermission[]) => {
		if (selectedRole) {
			const lockedPermissions = applyLockedPermissions(updated);
			const updatedRole = { ...selectedRole, permissions: lockedPermissions };
			setSelectedRole(updatedRole);
			dispatchRoles({
				type: 'UPDATE_PERMISSIONS',
				roleId: selectedRole.id,
				permissions: lockedPermissions,
			});
		}
	};

	const handleCreateSecurityRole = async (roleData: CreateSecurityRoleRequest) => {
		setCreateRoleLoading(true);
		setCreateRoleError(null);

		try {
			const newRole = await createSecurityRole(roleData);

			// Keep shared settings cache in sync
			await fetchSecurityRolesCached({ force: true });

			// Only add to local state if the new role belongs to current workplace
			const currentWorkplaceId = parseLocalWorkplaceId();
			const newRoleWorkplaceId = getWorkplaceIdFromString(newRole.workplace as any);
			if (currentWorkplaceId !== null && newRoleWorkplaceId === currentWorkplaceId) {
				// Don't add roles that contain the exact substring 'BYOD' (case-sensitive) to the visible list
				if (!(newRole?.name && String(newRole.name).includes('BYOD'))) {
					dispatchRoles({ type: 'ADD_ROLE', role: newRole });
				}
			}

			showToast('success', 'Security role created successfully');
			setViewMode('list');
			setCreateRoleLoading(false);
			handleBackClick();
		} catch (error: any) {
			console.error('Failed to create security role:', error);
			const errorMessage = error.message || 'Failed to create security role';
			setCreateRoleError(errorMessage);
			showToast('error', errorMessage);
			setCreateRoleLoading(false);
		}
	};

	const handleUpdateSecurityRole = async (roleToSave?: SecurityRole) => {
		const role = roleToSave || selectedRole;
		if (!role) return;

		setUpdateRoleLoading(true);
		setUpdateRoleError(null);

		try {
			// Map ModulePermission[] back to CreateSecurityRoleRequest format
			const allPermissions = _.flatten(
				applyLockedPermissions(role.permissions).map((perm) => perm.functions),
			);

			// Determine workplace value for API request.
			// If role.workplace already contains an API path, use it. Otherwise, map to the current workplace id.
			let workplaceVal: string | null = null;
			if (role.workplace) {
				if (typeof role.workplace === 'string' && role.workplace.startsWith('/api/workplaces/')) {
					workplaceVal = role.workplace;
				} else {
					const wpId = localStorage.getItem('workplace-id');
					workplaceVal = wpId ? `/api/workplaces/${wpId}` : null;
				}
			}

			const roleData: CreateSecurityRoleRequest = {
				name: role.name,
				description: role.description || '', // Include the description
				workplace: workplaceVal,
				securityRoleType: role.securityRoleType || undefined,
				securityRolePermission: allPermissions.map((perm) => ({
					permission: `/api/permissions/${perm.id}`,
					...perm.permissions,
				})),
			};

			await updateSecurityRole(role.id, roleData);

			// Refresh shared settings cache so other screens see updated roles/permissions
			await fetchSecurityRolesCached({ force: true });

			const { currentUserRole, refreshCurrentUserRole } = useUserDataStore.getState();
			if (isCurrentUsersSecurityRole(currentUserRole, role.id)) {
				await refreshCurrentUserRole();
				await queryClient.invalidateQueries('me');
			}

			// Update the role in the local state (name/description and permissions)
			dispatchRoles({
				type: 'UPDATE_ROLE',
				roleId: role.id,
				role: { name: role.name, description: role.description },
			});

			dispatchRoles({
				type: 'UPDATE_PERMISSIONS',
				roleId: role.id,
				permissions: role.permissions,
			});

			showToast('success', 'Security role updated successfully');
			handleBackClick();
			setViewMode('list');
			setUpdateRoleLoading(false);
		} catch (error: any) {
			console.error('Failed to update security role:', error);
			const errorMessage = error?.message || 'Failed to update security role';
			setUpdateRoleError(errorMessage);
			showToast('error', errorMessage);
			setUpdateRoleLoading(false);
		}
	};

	console.log('selected role:', selectedRole);

	// Check if user has permission to view Security Roles
	if (!canViewSecurity) {
		return (
			<div className={styles.workplaceCard}>
				<div className={styles.header}>
					<h2 className={styles.formTitle}>Security Roles</h2>
				</div>
				<div className={styles.noDataMessage}>
					You do not have permission to view Security Roles.
				</div>
			</div>
		);
	}

	switch (viewMode) {
		case 'list':
			return (
				<>
					{/* Security Roles Card */}
					<div className={styles.workplaceCard}>
						<div className={styles.header}>
							<h2 className={styles.formTitle}>Security Roles</h2>
							<Button
								className={styles.addButton}
								onClick={handleCreateClick}
								disabled={!canEditSecurity}
								title={
									canEditSecurity
										? 'Add new security role'
										: 'You do not have permission to add security roles'
								}
								style={{
									opacity: canEditSecurity ? 1 : 0.5,
									cursor: canEditSecurity ? 'pointer' : 'not-allowed',
								}}
							>
								Add
							</Button>
						</div>

						{rolesError && <div className={styles.errorMessage}>{rolesError}</div>}

						{rolesLoading ? (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									minHeight: '300px',
								}}
							>
								<CircularProgress size={20} color="inherit" />
							</Box>
						) : visibleRoles && visibleRoles.length !== 0 ? (
							<div className={styles.tableContainer} style={{ overflow: 'auto' }}>
								<table
									className={`${styles.workplaceTable} ${styles.userTable}`}
									style={{ tableLayout: 'auto' }}
								>
									<thead>
										<tr>
											<th style={{ width: '30%', minWidth: '150px' }}>Role Name</th>
											<th style={{ width: '45%', minWidth: '200px' }}>Description</th>
											<th style={{ width: '25%', minWidth: '120px' }}>Role Owner</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>View</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>Edit</th>
											<th style={{ width: '48px', padding: '8px', textAlign: 'center' }}>Delete</th>
										</tr>
									</thead>
									<tbody>
										{visibleRoles.map((role: SecurityRole) => (
											<tr key={role?.id}>
												<td style={{ whiteSpace: 'nowrap' }}>{role?.name}</td>
												<td>{role?.description}</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													{role?.workplace ? 'Workplace' : 'Organization'}
												</td>
												<td style={{ width: '48px', padding: '8px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => _handleShowClick(role)}
															title={
																canViewSecurity
																	? 'View Security Role'
																	: 'You do not have permission to view security roles'
															}
															disabled={!canViewSecurity}
															style={{
																opacity: canViewSecurity ? 1 : 0.5,
																cursor: canViewSecurity ? 'pointer' : 'not-allowed',
															}}
														>
															<Visibility className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ width: '48px', padding: '8px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleEditClick(role)}
															title={
																role.isSystemRole
																	? 'System roles cannot be edited'
																	: canEditSecurity
																		? 'Edit Security Role'
																		: 'You do not have permission to edit security roles'
															}
															disabled={!canEditSecurity || role.isSystemRole}
															style={{
																opacity: !canEditSecurity || role.isSystemRole ? 0.5 : 1,
																cursor:
																	!canEditSecurity || role.isSystemRole ? 'not-allowed' : 'pointer',
															}}
														>
															<Edit className={styles.editIcon} />
														</button>
													</div>
												</td>
												<td style={{ width: '48px', padding: '8px' }}>
													<div style={{ display: 'flex', justifyContent: 'center' }}>
														<button
															className={styles.iconButton}
															onClick={() => handleDeleteClick(role)}
															title={
																role.isSystemRole
																	? 'System roles cannot be deleted'
																	: canDeleteSecurity
																		? 'Delete Security Role'
																		: 'You do not have permission to delete security roles'
															}
															disabled={!canDeleteSecurity || role.isSystemRole}
															style={{
																opacity: !canDeleteSecurity || role.isSystemRole ? 0.5 : 1,
																cursor:
																	!canDeleteSecurity || role.isSystemRole
																		? 'not-allowed'
																		: 'pointer',
															}}
														>
															<Delete className={styles.deleteIcon} />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : !rolesLoading && !rolesError ? (
							<div className={styles.noDataMessage}>No security roles found.</div>
						) : null}

						{/* Delete Confirmation Dialog */}
						<Dialog
							open={deleteDialogOpen}
							onClose={handleDeleteCancel}
							aria-labelledby="delete-dialog-title"
							aria-describedby="delete-dialog-description"
						>
							<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
							<DialogContent>
								<DialogContentText id="delete-dialog-description">
									Are you sure you want to delete this security role?
									{roleToDelete && ` ${roleToDelete.name} will be permanently removed.`}
								</DialogContentText>
							</DialogContent>
							<DialogActions>
								<Button 
									className={styles.mabelButton}
									onClick={handleDeleteCancel}
								>
									Cancel
								</Button>
								<Button 
									className={styles.mabelButton}
									onClick={handleDeleteConfirm}
								>
									Delete
								</Button>
							</DialogActions>
						</Dialog>
					</div>
				</>
			);
		case 'create':
			return (
				<CreateSecurityRole
					backButtonClick={handleBackClick}
					onCreateRole={handleCreateSecurityRole}
					isLoading={createRoleLoading}
					error={createRoleError}
				/>
			);
		case 'edit':
			return (
				<div className={styles.showWorkplaceContainer}>
					{loadingEditPermissions ? (
						<div style={{ padding: '32px', textAlign: 'center', fontSize: '18px' }}>
							Loading permissions...
						</div>
					) : (
						selectedRole && (
							<Formik
								initialValues={{
									name: selectedRole.name || '',
									description: selectedRole.description || '',
									role_owner: selectedRole.workplace ? 'workplace' : 'organization',
									securityRoleType: selectedRole.securityRoleType || '',
								}}
								onSubmit={async (values, _helpers: FormikHelpers<any>) => {
									// update selectedRole locally and submit
									const updated: SecurityRole = {
										...selectedRole,
										name: values.name,
										description: values.description,
										securityRoleType: values.securityRoleType,
										// Map role_owner to workplace API path or null
										workplace:
											values.role_owner === 'workplace'
												? `/api/workplaces/${localStorage.getItem('workplace-id')}`
												: null,
									};
									setSelectedRole(updated);
									await handleUpdateSecurityRole(updated);
								}}
								validate={(values) => {
									const errors: Record<string, string> = {};
									// required name
									if (
										!values.name ||
										typeof values.name !== 'string' ||
										values.name.trim() === ''
									) {
										errors.name = SECURITY_ROLE_VALIDATION_MESSAGES.NAME_REQUIRED;
									} else if (values.name.length < SECURITY_ROLE_NAME_MIN_LENGTH) {
										errors.name = SECURITY_ROLE_VALIDATION_MESSAGES.NAME_MIN;
									} else if (values.name.length > SECURITY_ROLE_NAME_MAX_LENGTH) {
										errors.name = SECURITY_ROLE_VALIDATION_MESSAGES.NAME_MAX;
									} else if (!SECURITY_ROLE_NAME_PATTERN.test(values.name)) {
										errors.name = SECURITY_ROLE_VALIDATION_MESSAGES.NAME_PATTERN;
									}
									// required securityRoleType
									if (!values.securityRoleType || values.securityRoleType.trim() === '') {
										errors.securityRoleType = 'Notifications Level for Role is required';
									}
									// optional description length limit
									if (
										values.description &&
										values.description.length > SECURITY_ROLE_DESCRIPTION_MAX_LENGTH
									) {
										errors.description = SECURITY_ROLE_VALIDATION_MESSAGES.DESCRIPTION_MAX;
									}
									return errors;
								}}
							>
								{({ values, errors, handleChange, handleSubmit, touched }) => (
									<>
										<div className={styles.header}>
											<Grid container justifyContent="space-between" alignItems="center">
												<Grid item xs={12} style={{ display: 'flex', gap: '10px' }}>
													<Grid xs={6}>
														<Button className={styles.backButton} onClick={handleBackClick}>
															Back
														</Button>
													</Grid>
													<Grid xs={6} textAlign={'right'}>
														<Button
															className={styles.saveButton}
															onClick={() => handleSubmit()}
															disabled={
																updateRoleLoading || (selectedRole && selectedRole.isSystemRole)
															}
														>
															{updateRoleLoading ? 'Saving...' : 'Save'}
														</Button>
													</Grid>
												</Grid>
												<Grid item xs={12}>
													<h2 className={styles.formTitle}>
														Editing security role : {values.name}{' '}
													</h2>
												</Grid>
											</Grid>
										</div>
										{updateRoleError && (
											<div className={styles.errorMessage} style={{ marginBottom: '16px' }}>
												{updateRoleError}
											</div>
										)}
										<Grid container spacing={2} sx={{ mb: 3 }}>
											<Grid item xs={12}>
												<TextField
													label="Role Name"
													fullWidth
													variant="outlined"
													margin="normal"
													name="name"
													value={values.name}
													onChange={handleChange}
													error={Boolean(touched.name && errors.name)}
													inputProps={{ maxLength: SECURITY_ROLE_NAME_MAX_LENGTH }}
													helperText={
														touched.name && typeof errors.name === 'string'
															? errors.name
															: `${(values.name || '').length}/${SECURITY_ROLE_NAME_MAX_LENGTH}`
													}
												/>
											</Grid>
											<Grid item xs={12}>
												<FormControl 
													component="fieldset" 
													margin="normal" 
													fullWidth
													disabled={!selectedRole.workplace}
												>
													<FormLabel component="legend">Role Owner</FormLabel>
													<RadioGroup
														row
														name="role_owner"
														value={values.role_owner}
														onChange={handleChange}
													>
														<FormControlLabel
															value="workplace"
															control={<Radio />}
															label="Workplace"
														/>
														<FormControlLabel
															value="organization"
															control={<Radio />}
															label="Organization"
														/>
													</RadioGroup>
												</FormControl>
											</Grid>
											<Grid item xs={12}>
												<Autocomplete
													options={[...securityRoleTypes].sort((a, b) => 
														a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
													)}
													getOptionLabel={(option) => {
														if (typeof option === 'string') return option;
														return option.label || '';
													}}
													value={securityRoleTypes.find((type) => type?.['@id'] === values.securityRoleType) || null}
													onChange={(_, value) => {
														const event = {
															target: {
																name: 'securityRoleType',
																value: value ? value?.['@id'] : ''
															}
														};
														handleChange(event);
													}}
													isOptionEqualToValue={(option, value) => option?.['@id'] === value?.['@id']}
													renderInput={(params) => (
														<TextField
															{...params}
															label="Notifications Level for Role"
															name="securityRoleType"
															margin="normal"														required															error={touched.securityRoleType && Boolean(errors.securityRoleType)}
															helperText={
																touched.securityRoleType && typeof errors.securityRoleType === 'string'
																	? errors.securityRoleType
																	: ''
															}
															fullWidth
														/>
													)}
												/>
											</Grid>{' '}
											<Grid item xs={12}>
												<TextField
													label="Description"
													fullWidth
													multiline
													rows={3}
													variant="outlined"
													margin="normal"
													name="description"
													value={values.description}
													onChange={handleChange}
													error={Boolean(touched.description && errors.description)}
													inputProps={{ maxLength: SECURITY_ROLE_DESCRIPTION_MAX_LENGTH }}
													helperText={
														touched.description && typeof errors.description === 'string'
															? errors.description
															: `${(values.description || '').length}/${SECURITY_ROLE_DESCRIPTION_MAX_LENGTH}`
													}
												/>
											</Grid>
										</Grid>
										<Grid className={styles.tableContainer}>
											<SecurityRoleMatrix
												data={selectedRole.permissions}
												onChange={handlePermissionChange}
											/>
										</Grid>
									</>
								)}
							</Formik>
						)
					)}
				</div>
			);
		default:
			return (
				<div className={styles.showWorkplaceContainer}>
					<div className={styles.header}>
						<h2 className={styles.formTitle}>{selectedRole.name} Details</h2>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</div>
					{selectedRole && (
						<>
							<Grid className={styles.tableContainer}>
								<SecurityRoleMatrix
									data={selectedRole.permissions}
									onChange={handlePermissionChange}
									readonly={true}
								/>
							</Grid>
						</>
					)}
				</div>
			);
	}
}

export default SecurityRoles;
